import {
  FlagIcon,
  ChatBubbleLeftEllipsisIcon,
  ShieldExclamationIcon,
  PhoneIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import { setActiveTab } from 'common/slice/activeTabSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { TABS } from './constant';
import { setActiveAttendee } from 'common/slice/activeAttendeeSlice';
import { updateActiveAttendee } from 'common/slice/selectedAttendeeSlice';
import { addBreadcrumb } from 'common/slice/breadcrumbsSlice';
import { Tooltip } from 'react-tooltip';
import { getAudioCallStatus } from 'helpers/messages';
import { openModal } from 'common/slice/modalSlice';
import { SessionStatus } from 'globals/enums';
import { isSessionEnded } from 'helpers/session';

interface Props {
  attendee: any;
  isApprovalPending: boolean;
  isChatUnread: boolean;
  tooltipId: string;
  sessionId: string;
  sessionUuid: string;
  sessionStatus: string;
}

const AttendeePane = ({
  attendee,
  isApprovalPending,
  isChatUnread,
  tooltipId,
  sessionId,
  sessionUuid,
  sessionStatus,
}: Props) => {
  const dispatch = useAppDispatch();
  const audioCall = useAppSelector((state) => state.audioCall);
  const { isDetailView } = useAppSelector((state) => state.selectedAttendee);
  const activeTab = useAppSelector((state) => state.activeTab.tab);
  const tooltip = tooltipId ? tooltipId : `tile_tooltip_${attendee.attendee_id}`;
  const actionTooltipId = tooltipId
    ? `action_${tooltipId}`
    : `tile_tooltip_action_${attendee.attendee_id}`;

  const audioCallStatus = getAudioCallStatus(sessionId, audioCall.audioCallStatuses);
  const isMarkedAsNoShow = sessionStatus === SessionStatus.NoShow;

  const handleChange = (value: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (isMarkedAsNoShow) {
      return;
    }
    dispatch(setActiveTab(value));
    dispatch(setActiveAttendee(attendee));
    dispatch(
      updateActiveAttendee({
        attendeeId: attendee.attendee_id,
        isDetailView: isDetailView,
      }),
    );
    const payload = {
      label: attendee.attendee_number,
      url: '',
    };
    if (isDetailView) {
      dispatch(addBreadcrumb(payload));
    }
  };

  const handleAction = (event: React.MouseEvent, type: string) => {
    event.stopPropagation();
    if (isMarkedAsNoShow) {
      return;
    }
    dispatch(
      openModal({
        attendeeId: attendee.attendee_id,
        type,
        sessionUuid,
        sessionId,
      }),
    );
  };

  return (
    <>
      <div
        className="cursor-pointer"
        onClick={(event) => handleChange(activeTab, event)}
        data-tooltip-id={tooltip}
      >
        {attendee.attendee_number}
      </div>
      <Tooltip className="z-[1000]" id={tooltip} place="bottom">
        <div className="text-xs flex flex-col gap-1">
          <div>
            Name: {attendee.attendee_first_name ? attendee.attendee_first_name : '--'}{' '}
            {attendee.attendee_last_name ? attendee.attendee_last_name : '--'}
          </div>
          <div>Email: {attendee.attendee_email ? attendee.attendee_email : '--'}</div>
          <div>
            Attendee ID: {attendee.attendee_external_id ? attendee.attendee_external_id : '--'}
          </div>
        </div>
      </Tooltip>
      <FlagIcon
        className="h-4 w-4 cursor-pointer"
        onClick={(event) => handleChange(TABS.ALERT_LOGS, event)}
      />
      <ChatBubbleLeftEllipsisIcon
        className={`h-4 w-4 cursor-pointer ${isChatUnread ? 'text-red-500' : ''}`}
        onClick={(event) => handleChange(TABS.CHAT, event)}
      />

      <ShieldExclamationIcon
        className={`h-4 w-4 cursor-pointer ${isApprovalPending ? 'text-red-500' : ''}`}
        onClick={(event) => handleChange(TABS.APPROVALS, event)}
      />
      <PhoneIcon
        className={`h-4 w-4 cursor-pointer ${
          audioCallStatus === 'requested'
            ? 'text-yellow-500 fill-current'
            : audioCallStatus === 'connected'
            ? 'text-green-500 fill-current'
            : ''
        }`}
        onClick={(event) => handleChange(TABS.CHAT, event)}
      />
      <EllipsisVerticalIcon
        data-tooltip-id={actionTooltipId}
        onClick={(event) => event.stopPropagation()}
        className={`block h-4 w-4 cursor-pointer ${isChatUnread ? 'text-red-500' : ''}`}
      />
      {sessionStatus !== SessionStatus.NoShow && (
        <Tooltip place="right" clickable className="action-tooltip" id={actionTooltipId}>
          <div className="text-sm z-10 gap-5 text-slate-500">
            {!sessionId && (
              <p
                onClick={(event) => handleAction(event, SessionStatus.NoShow)}
                className="cursor-pointer p-2 hover:bg-slate-100"
              >
                Mark as No-Show
              </p>
            )}
            {sessionId && !isSessionEnded(sessionStatus) && (
              <p
                onClick={(event) => handleAction(event, SessionStatus.Terminated)}
                className="cursor-pointer p-2 hover:bg-slate-100"
              >
                Terminate
              </p>
            )}
            {sessionId && (
              <p
                className="cursor-pointer p-2 hover:bg-slate-100"
                onClick={(event) => handleAction(event, 'add-flag')}
              >
                Add Flag
              </p>
            )}
          </div>
        </Tooltip>
      )}
    </>
  );
};

export default AttendeePane;
